import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";

import { AuthProvider, useAuth } from "./auth/AuthContext";
import LoginPage from "./pages/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import InformationsDashboard from "./pages/AdminDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import Navbar from "./layout/Navbar";
import Dashboard from "./features/dashboard/Dashboard";
import RobotTabs from "./features/robot/RobotTabs";
import EtiquetageProduction from "./features/etiquetage/EtiquetageProduction";
import HorsPilulier from "./features/horsPilulier/HorsPilulier";
import BonLivraison from "./features/bonLivraison/BonLivraison";
import SuiviLivraison from "./features/suiviLivraison/SuiviLivraison";
import Livreur from "./features/livreur/Livreur";
import Reclamation from "./features/reclamation/Reclamation";
import ValidationPharma from "./features/validationPharma/ValidationPharma";
import Primo from "./features/primo/Primo";
import Administration from "./features/administration/Administration";
import EspacePersonnel from "./features/espacePersonnel/EspacePersonnel";
import MaterielMedical from "./features/materielMedical/MaterielMedical";
import Controle from "./features/controle/Controle";
import ControleBis from "./features/controle/ControleBis";
import DossierLotProduction from "./features/productionDossierLot/DossierLotProduction";
import Cno from "./features/cno/Cno";
import SuiviConformite from "./features/suiviConformite/SuiviConformite";
import { API } from "./services/api";
import { useBreakpoint } from "./hooks/useBreakpoint";
import { useVersionCheck } from "./hooks/useVersionCheck";
import UpdateToast from "./components/UpdateToast";

const PHARMACY_ROLES = ["TITULAIRE", "ADMIN", "PHARMACIEN", "SUPER_ADMIN"];
const PRODUCTION_ROLES = [...PHARMACY_ROLES, "PRODUCTEUR"];
const DELIVERY_ROLES = [...PHARMACY_ROLES, "LIVREUR"];
const CONFORMITY_ROLES = ["TITULAIRE", "SUPER_ADMIN"];

function ServicePage() {
    const { serviceKey } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        API.get("/admin/services", { noRedirectOn401: true })
            .then(({ data }) => {
                if (!mounted) return;
                setService((data || []).find(item => item.serviceKey === serviceKey) || null);
            })
            .catch(() => { if (mounted) setService(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [serviceKey]);

    if (loading) {
        return <div style={{ padding: 24, color: "#64748b", fontSize: 14 }}>Chargement du service...</div>;
    }

    if (service?.type === "CONTROLE") return <Controle service={service} />;
    if (service?.type === "DOSSIER_LOT_PRODUCTION") return <DossierLotProduction service={service} />;
    return <MaterielMedical />;
}

function AppShell() {
    const { isMobile } = useBreakpoint();
    const { user } = useAuth();
    const updateAvailable = useVersionCheck();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        if (user?.role === "PRODUCTEUR" && !["/robot-production", "/robot-planning", "/controle", "/controle-bis"].includes(location.pathname)) {
            navigate("/robot-production", { replace: true });
        }
        if (user?.role === "LIVREUR" && location.pathname !== "/livreur") {
            navigate("/livreur", { replace: true });
        }
    }, [user?.role, location.pathname, navigate]);

    return (
        <div style={{ display: "flex", flexDirection: "row", height: isMobile ? "100svh" : "100vh", position: "relative" }}>
            {/* Mobile backdrop */}
            {isMobile && mobileNavOpen && (
                <button
                    type="button"
                    aria-label="Fermer le menu"
                    onClick={() => setMobileNavOpen(false)}
                    style={{
                        position: "fixed", inset: isMobile ? "0 0 auto" : 0,
                        height: isMobile ? "100svh" : undefined,
                        background: "rgba(12, 59, 60, 0.46)",
                        zIndex: 99,
                        border: 0,
                        padding: 0,
                    }}
                />
            )}

            <Navbar
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
            />

            <div style={{ flex: 1, overflowY: "auto", minWidth: 0, display: "flex", flexDirection: "column" }}>
                {/* Mobile topbar */}
                {isMobile && (
                    <div style={{
                        height: "48px", flexShrink: 0,
                        background: "#0C3B3C",
                        display: "flex", alignItems: "center",
                        padding: "0 16px", gap: "12px",
                        borderBottom: "2px solid #17B978",
                    }}>
                        <button
                            onClick={() => setMobileNavOpen(v => !v)}
                            style={{
                                background: "none", border: "none", cursor: "pointer",
                                color: "white", fontSize: "20px", padding: "4px",
                                display: "flex", alignItems: "center", lineHeight: 1,
                            }}
                        >
                            ☰
                        </button>
                        <img
                            src="/logotype-horizontal-blanc.svg"
                            alt="SUIVI QUALITÉ"
                            style={{ display: "block", width: 118, height: "auto" }}
                        />
                    </div>
                )}

                {updateAvailable && <UpdateToast />}

                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    <Routes>
                        <Route path="/" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <Dashboard />
                            </ProtectedRoute>
                        } />
                        <Route path="/robot-production" element={
                            <ProtectedRoute allowedRoles={PRODUCTION_ROLES}>
                                <RobotTabs />
                            </ProtectedRoute>
                        } />
                        <Route path="/robot-planning" element={
                            <ProtectedRoute allowedRoles={PRODUCTION_ROLES}>
                                <RobotTabs />
                            </ProtectedRoute>
                        } />
                        <Route path="/etiquetage-production" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <EtiquetageProduction />
                            </ProtectedRoute>
                        } />
                        <Route path="/hors-pilulier" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <HorsPilulier />
                            </ProtectedRoute>
                        } />
                        <Route path="/bon-livraison" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <BonLivraison />
                            </ProtectedRoute>
                        } />
                        <Route path="/bon-livraison/historique" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <BonLivraison />
                            </ProtectedRoute>
                        } />
                        <Route path="/bon-livraison/planning" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <BonLivraison />
                            </ProtectedRoute>
                        } />
                        <Route path="/suivi" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <SuiviLivraison />
                            </ProtectedRoute>
                        } />
                        <Route path="/suivi-livraison" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <SuiviLivraison />
                            </ProtectedRoute>
                        } />
                        <Route path="/livreur" element={
                            <ProtectedRoute allowedRoles={DELIVERY_ROLES}>
                                <Livreur />
                            </ProtectedRoute>
                        } />
                        <Route path="/reclamation" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <Reclamation />
                            </ProtectedRoute>
                        } />
                        <Route path="/validation-pharma" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <ValidationPharma />
                            </ProtectedRoute>
                        } />
                        <Route path="/primo" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <Primo />
                            </ProtectedRoute>
                        } />
                        <Route path="/cno" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <Cno />
                            </ProtectedRoute>
                        } />
                        <Route path="/services/:serviceKey" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <ServicePage />
                            </ProtectedRoute>
                        } />
                        <Route path="/informations" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <InformationsDashboard />
                            </ProtectedRoute>
                        } />
                        <Route path="/administration" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <Administration />
                            </ProtectedRoute>
                        } />
                        <Route path="/espace-personnel" element={
                            <ProtectedRoute allowedRoles={PHARMACY_ROLES}>
                                <EspacePersonnel />
                            </ProtectedRoute>
                        } />
                        <Route path="/suivi-conformite" element={
                            <ProtectedRoute allowedRoles={CONFORMITY_ROLES}>
                                <SuiviConformite />
                            </ProtectedRoute>
                        } />
                        <Route path="/controle" element={
                            <ProtectedRoute allowedRoles={PRODUCTION_ROLES}>
                                <Controle />
                            </ProtectedRoute>
                        } />
                        <Route path="/controle-bis" element={
                            <ProtectedRoute allowedRoles={PRODUCTION_ROLES}>
                                <ControleBis />
                            </ProtectedRoute>
                        } />
                        <Route path="/admin" element={<Navigate to="/informations" replace />} />
                        <Route path="/super-admin" element={
                            <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                                <SuperAdminDashboard />
                            </ProtectedRoute>
                        } />
                    </Routes>
                </div>
            </div>
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/*" element={
                        <ProtectedRoute>
                            <AppShell />
                        </ProtectedRoute>
                    } />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;
